import React, { useState } from 'react';
import { UserPlus, Check, ExternalLink, AlertCircle } from 'lucide-react';
import axios from 'axios';

const JoinUs = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone_number: '',
    message: '',
    interest_types: []
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleInterestChange = (interest) => {
    setFormData(prev => ({
      ...prev,
      interest_types: prev.interest_types.includes(interest)
        ? prev.interest_types.filter(i => i !== interest)
        : [...prev.interest_types, interest]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await axios.post('/join-us', formData);
      setSuccess(true);
      
      if (response.data.whatsapp_url) {
        setWhatsappUrl(response.data.whatsapp_url);
      }
      
      // Reset form
      setFormData({
        full_name: '',
        email: '',
        phone_number: '',
        message: '',
        interest_types: []
      });
    } catch (error) {
      setError(error.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const interestOptions = [
    { id: 'player', label: 'Player', description: 'Join our squad and represent Crawley Athletic' },
    { id: 'volunteer', label: 'Volunteer/Staff', description: 'Help run the club behind the scenes' },
    { id: 'sponsor', label: 'Sponsor', description: 'Support the club through sponsorship opportunities' }
  ];

  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-lg p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Check className="h-8 w-8 text-green-600" />
            </div>
            
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Thank You for Your Interest!
            </h2>
            
            <p className="text-lg text-gray-600 mb-8">
              We've received your submission and someone from our team will be in touch soon.
            </p>

            {whatsappUrl && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-800 mb-2">
                  Instant Contact Available
                </h3>
                <p className="text-green-700 mb-4">
                  For immediate assistance, you can also reach us directly via WhatsApp.
                </p>
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary bg-green-600 hover:bg-green-700 border-green-600"
                >
                  <ExternalLink className="h-5 w-5" />
                  Contact via WhatsApp
                </a>
              </div>
            )}

            <div className="space-y-4 text-left">
              <h3 className="text-lg font-semibold text-gray-900">What happens next?</h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-black text-white rounded-full text-sm flex items-center justify-center mt-0.5 mr-3">1</span>
                  Our team will review your submission within 24-48 hours
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-black text-white rounded-full text-sm flex items-center justify-center mt-0.5 mr-3">2</span>
                  We'll contact you to discuss opportunities that match your interests
                </li>
                <li className="flex items-start">
                  <span className="w-6 h-6 bg-black text-white rounded-full text-sm flex items-center justify-center mt-0.5 mr-3">3</span>
                  If you're interested in playing, we'll arrange a trial or meeting
                </li>
              </ul>
            </div>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setSuccess(false);
                  setWhatsappUrl('');
                }}
                className="btn btn-secondary"
              >
                Submit Another Application
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <UserPlus className="h-16 w-16 mx-auto mb-4" />
          <h1 className="text-4xl font-bold mb-4">Join Crawley Athletic</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto">
            Be part of our journey. Whether you want to play, volunteer, or sponsor, 
            there's a place for you in the Crawley Athletic family.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Interest Options */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-center mb-8">How would you like to get involved?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {interestOptions.map((option) => (
              <div
                key={option.id}
                className={`card cursor-pointer transition-all ${
                  formData.interest_types.includes(option.id)
                    ? 'ring-2 ring-black bg-gray-50'
                    : 'hover:shadow-lg'
                }`}
                onClick={() => handleInterestChange(option.id)}
              >
                <div className="text-center">
                  <div className={`w-6 h-6 rounded-full border-2 mx-auto mb-4 ${
                    formData.interest_types.includes(option.id)
                      ? 'bg-black border-black'
                      : 'border-gray-300'
                  }`}>
                    {formData.interest_types.includes(option.id) && (
                      <Check className="h-4 w-4 text-white m-0.5" />
                    )}
                  </div>
                  <h3 className="text-lg font-semibold mb-2">{option.label}</h3>
                  <p className="text-gray-600 text-sm">{option.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h2 className="text-2xl font-bold mb-6">Tell us about yourself</h2>
          
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-red-400" />
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-group">
                <label htmlFor="full_name" className="form-label">
                  Full Name *
                </label>
                <input
                  type="text"
                  id="full_name"
                  name="full_name"
                  required
                  className="form-input"
                  placeholder="Enter your full name"
                  value={formData.full_name}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  className="form-input"
                  placeholder="your.email@example.co.uk"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="phone_number" className="form-label">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone_number"
                name="phone_number"
                required
                className="form-input"
                placeholder="+44 123 456 7890"
                value={formData.phone_number}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group">
              <label htmlFor="message" className="form-label">
                Tell us more about yourself *
              </label>
              <textarea
                id="message"
                name="message"
                required
                rows={6}
                className="form-textarea"
                placeholder="Tell us about your football experience, why you want to join Crawley Athletic, your availability, or any other relevant information..."
                value={formData.message}
                onChange={handleInputChange}
              />
            </div>

            <div className="bg-gray-50 rounded-lg p-6">
              <h3 className="text-lg font-semibold mb-4">GDPR Consent</h3>
              <div className="space-y-3 text-sm text-gray-600">
                <p>
                  By submitting this form, you consent to Crawley Athletic Football Club 
                  processing your personal data for the purposes of:
                </p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Reviewing your application and contacting you about opportunities</li>
                  <li>Maintaining records of potential players, volunteers, and sponsors</li>
                  <li>Sending you relevant club communications (if you join us)</li>
                </ul>
                <p>
                  You can withdraw your consent at any time by emailing{' '}
                  <a href="mailto:info@crawleyafc.uk" className="text-black hover:underline">
                    info@crawleyafc.uk
                  </a>
                </p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                type="submit"
                disabled={loading || formData.interest_types.length === 0}
                className={`btn btn-primary ${loading ? 'loading' : ''} ${
                  formData.interest_types.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {loading ? 'Submitting...' : 'Submit Application'}
              </button>
              
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setFormData({
                  full_name: '',
                  email: '',
                  phone_number: '',
                  message: '',
                  interest_types: []
                })}
              >
                Clear Form
              </button>
            </div>

            {formData.interest_types.length === 0 && (
              <p className="text-center text-sm text-red-600">
                Please select at least one area of interest above
              </p>
            )}
          </form>
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center">
          <h3 className="text-lg font-semibold mb-4">Prefer to contact us directly?</h3>
          <div className="space-y-2">
            <a 
              href="mailto:info@crawleyafc.uk" 
              className="block text-black hover:underline font-medium"
            >
              info@crawleyafc.uk
            </a>
            <p className="text-gray-600">
              We'll respond to all enquiries within 48 hours
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinUs;